import React, { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';

import scss from './MessagingConciergePreferencesModal.module.scss';
import { callApi } from '@api/index';
import { CONCIERGE_IMAGE } from './Constants';
import { BUDGETS, convertBudgetAmountToArrayValue } from '@helper/budgetHelper';
import Loading from '@ui/Loading';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { redirectToInbox } from '@helper/urlHelpers';

interface ConciergeType {
    id: string;
    label: string;
    selected: boolean;
    icon: string;
}

interface RadioGroupItemProps extends ConciergeType {
    onSelection: (id: string) => void;
}

export interface MessagingConciergePreferencesModalProps {
    handleClose: () => void;
}

interface FormData {
    messagingNumberOfGuests: string;
    messagingBudgetRange: string;
    messagingMobileNumber: string;
    messagingConciergeType: string;
}

const conciergeTypes: ConciergeType[] = [
    {
        id: 'venue',
        label: 'Finding \na Venue',
        selected: false,
        icon: 'fal fa-church'
    },
    {
        id: 'packages',
        label: 'Booking \nSuppliers',
        selected: false,
        icon: 'fal fa-store'
    },
    {
        id: 'destination',
        label: 'Destination \nWedding',
        selected: false,
        icon: 'fal fa-plane'
    },
    {
        id: 'other',
        label: 'Something \nElse',
        selected: false,
        icon: 'fal fa-message'
    },
];

const RadioGroupItem = ({ id, label, selected, icon, onSelection }: RadioGroupItemProps) => {
    return (
        <div onClick={() => onSelection(id)} className={`${scss.conciergeTypeItem} ${selected ? scss.conciergeTypeItemActive : ''}`}>
            <div className={scss.conciergeTypeImage}>
                <i className={icon}></i>
            </div>
            <div className={scss.conciergeTypeText}>{label}</div>
        </div>
    );
}

function suggestLogout() {
    if(confirm('This is a new feature that requires you to log out and log back in.')) {
        window.location.href = '/logout';
    }
}

const useCoupleData = (setFormData: React.Dispatch<React.SetStateAction<FormData>>) => {
    const [loading, setLoading] = useState<boolean>(false);
    useEffect(() => {
        setLoading(true);
        callApi('/api/EasyDirectoryAPI/couple-details').then(data => {
            setFormData(formData => ({
                messagingNumberOfGuests: !!data?.numberOfGuests ? data.numberOfGuests : '',
                messagingBudgetRange: !!data?.budgetAmount ? data.budgetAmount : '',
                messagingMobileNumber: !!data?.phone ? data.phone : '',
                messagingConciergeType: formData.messagingConciergeType
            }));
        }).finally(() => setLoading(false));
    }, []);
    return { loading };
}

export const MessagingConciergePreferences: React.FC = () => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitFailed, setSubmitFailed] = useState<boolean>(false);
    const [conciergeOptions, setConciergeOptions] = useState<ConciergeType[]>(conciergeTypes);

    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
    const [formValid, setFormValid] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({
        messagingNumberOfGuests: '',
        messagingBudgetRange: '',
        messagingMobileNumber: '',
        messagingConciergeType: ''
    });

    const { loading } = useCoupleData(setFormData);

    
    const handleConciergeSelection = (id: string) => {
        const updatedOptions = conciergeOptions.map(option => {
            return {
                ...option,
                selected: option.id === id
            };
        });

        setConciergeOptions(updatedOptions);
        setFormDataValue('messagingConciergeType', id);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormDataValue(event.target.id, event.target.value);
    };

    const setFormDataValue = (key: string, value: string) => {
        setFormData(current => {
            const data = ({
                ...current,
                [key]: value,
            });
            checkFormValidity(data);
            return data;
        });
    };

    const checkFormValidity = (data: FormData) => {
        const isValid = Object.values(data).every(value => !!value);
        setFormValid(isValid);
    };

    const submissionFailed = () => {
        setSubmitFailed(true);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormSubmitted(true);

        if (!formValid) {
            return;
        }

        setSubmitting(true);

        axios.post('/api/EasyDirectoryAPI/update-concierge-preference', {
            noOfGuests: formData.messagingNumberOfGuests,
            budget: formData.messagingBudgetRange,
            phone: formData.messagingMobileNumber,
            venue: formData.messagingConciergeType === 'venue',
            packages: formData.messagingConciergeType === 'packages',
            destinationWeddings: formData.messagingConciergeType === 'destination',
            other: formData.messagingConciergeType === 'other'
        }, {
            headers: { 'Content-Type': 'application/json' } 
        })
        .then((response) => {
            if(response.status === 200) {
                redirectToInbox();
            }
            else {
                submissionFailed();
                setSubmitting(false);
                suggestLogout();
            }
        })
        .catch(() => {
            submissionFailed();
            setSubmitting(false);
            suggestLogout();
        });
    };

    return (<>
        {(!!window?.location?.pathname && window.location.pathname.startsWith('/dashboard/')) && <div className={`d-block d-sm-none ${scss.mobileBreadcrumbs}`}>
            <Link to="/"><strong>←</strong> Dashboard</Link>
        </div>}
        <div className={scss.headingContainer}>
            <div className={scss.imageContainer}>
                <img className={scss.imageDarcy} src={CONCIERGE_IMAGE} alt="Ask Darcy" />
            </div>
            <h2 className={scss.headingText}>Let's Start Planning</h2>
            <p className={scss.headingSubText}>Please provide a few details so I can better assist you on your planning journey.</p>
        </div>
        <hr />
        {loading ?
            <div className={'my-3 w-100 d-flex justify-content-center'}>
                <Loading />
            </div> :
            <Form onSubmit={handleSubmit}>
                <Row className={scss.mobileRow}>
                    <Form.Group as={Col} className={'mb-3'} controlId={'messagingNumberOfGuests'}>
                        <Form.Label>Number of Guests</Form.Label>
                        <Form.Control
                            required
                            type={'number'}
                            value={formData.messagingNumberOfGuests}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => handleInputChange(event)}
                        />
                        {formSubmitted && !formData.messagingNumberOfGuests && <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>}
                    </Form.Group>
                    <Form.Group as={Col} className={'mb-3'} controlId={'messagingBudgetRange'}>
                        <Form.Label>Budget Range</Form.Label>
                        <Form.Select
                            required
                            value={!!formData.messagingBudgetRange ? convertBudgetAmountToArrayValue(Number(formData.messagingBudgetRange)) : formData.messagingBudgetRange}
                            onChange={(event: ChangeEvent<HTMLSelectElement>) => handleInputChange(event)}
                        >
                            <option value="">Select budget range</option>
                            {BUDGETS.map(budget => <option key={budget.value} value={budget.value}>{budget.label}</option>)}
                        </Form.Select>
                        {formSubmitted && !formData.messagingBudgetRange && <Form.Control.Feedback type="invalid">This field is required</Form.Control.Feedback>}
                    </Form.Group>
                </Row>
                <Form.Group as={Col} className={'mb-3'} controlId={'messagingMobileNumber'}>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control 
                        required
                        type={'tel'} 
                        pattern={'^04\\d{8}$'}
                        placeholder={'Enter mobile number'}
                        value={formData.messagingMobileNumber}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleInputChange(event)}
                    />
                    <Form.Control.Feedback type="invalid">
                        Please enter a valid Australian mobile number.
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Label>Can I help you with any of these tasks?</Form.Label>
                {formSubmitted && !formData.messagingConciergeType && <small className={'ms-1 text-danger'}>This field is required</small>}
                <Form.Group as={Col} className={scss.conciergeTypeContainer} controlId={'messagingConciergeType'}>
                    {conciergeOptions.map(option => <RadioGroupItem key={option.id} {...option} onSelection={(id) => handleConciergeSelection(id)} />)}
                </Form.Group>
                <Button variant={'primary'} type={'submit'} className={`w-100 ${scss.submitBtn}`} disabled={submitting}>
                    {submitting ? <Loading size={'sm'} /> : 'Continue'}
                </Button>
                {submitFailed && <small className={'text-danger mt-3 d-block text-center'}>An error occurred, please try again later.</small>}
            </Form>
        }
    </>);
}

const MessagingConciergePreferencesModal: React.FC<MessagingConciergePreferencesModalProps> = ({ handleClose }) => {
    return (
        <Modal centered={true} show onHide={handleClose} backdrop={'static'} name="concierge_onboarding_modal">
            <Modal.Header closeButton={false} className="border-0 justify-content-end pb-0">
                <Button variant="link" className="text-shade" onClick={handleClose}>
                    Close <i className="fal fa-times fa-2xl text-primary ms-1"></i>
                </Button>
            </Modal.Header>
            <Modal.Body className={scss.modal}>
                <MessagingConciergePreferences />
            </Modal.Body>
        </Modal>
    )
}

export default MessagingConciergePreferencesModal
